/**
 * Wraps Daily's call-object (headless) SDK behind the provider-agnostic VideoSession
 * interface, so nothing outside this file needs to import Daily's types.
 *
 * Identity mapping: the worker mints each participant's Daily meeting token with
 * `user_id` set to their Merels clientId (see apps/worker/src/deps/video/dailyClient).
 * Daily reports that clientId back as `participant.user_id` on every participant event,
 * which is what we key the `participants` signal by - not Daily's own session_id.
 */
import Daily, {
  type DailyCall,
  type DailyParticipant,
  type DailyEventObjectParticipant,
  type DailyEventObjectParticipantLeft,
  type DailyEventObjectTrack,
  type DailyEventObjectAvailableDevicesUpdated,
  type DailyEventObjectSelectedDevicesUpdated,
} from '@daily-co/daily-js'
import { signal } from '@preact/signals-core'
import { EMPTY_DEVICE_STATE } from './types'
import type { VideoConnectionState, VideoDeviceState, VideoParticipant, VideoSession, VideoSessionConfig } from './types'

function toVideoParticipant(participant: DailyParticipant): VideoParticipant {
  const video = participant.tracks?.video
  const audio = participant.tracks?.audio
  return {
    clientId: participant.user_id,
    hasVideo: video?.state === 'playable',
    hasAudio: audio?.state === 'playable',
    videoTrack: video?.persistentTrack,
    audioTrack: audio?.persistentTrack,
    isLocal: participant.local,
  }
}

export function makeVideoSession(config: VideoSessionConfig): VideoSession {
  const { roomUrl, token, role } = config

  const participants = signal(new Map<string, VideoParticipant>())
  const localState = signal({ cameraOn: role === 'solo', micOn: role !== 'viewer' })
  const connectionState = signal<VideoConnectionState>('idle')
  const devices = signal<VideoDeviceState>(EMPTY_DEVICE_STATE)

  let callObject: DailyCall | null = null

  function upsertParticipant(participant: DailyParticipant): void {
    const next = new Map(participants.value)
    next.set(participant.user_id, toVideoParticipant(participant))
    participants.value = next
  }

  function removeParticipant(participant: DailyParticipant): void {
    const next = new Map(participants.value)
    next.delete(participant.user_id)
    participants.value = next
  }

  // Device labels are only populated by the browser once mic/camera permission has
  // been granted, which has already happened by the time join() resolves.
  async function refreshDevices(): Promise<void> {
    if (!callObject) return
    const [{ devices: allDevices }, current] = await Promise.all([
      callObject.enumerateDevices(),
      callObject.getInputDevices(),
    ])
    const toOption = (d: MediaDeviceInfo) => ({ deviceId: d.deviceId, label: d.label || `${d.kind} ${d.deviceId.slice(0, 6)}` })
    devices.value = {
      cameras: allDevices.filter((d) => d.kind === 'videoinput').map(toOption),
      mics: allDevices.filter((d) => d.kind === 'audioinput').map(toOption),
      selectedCameraId: 'deviceId' in current.camera ? current.camera.deviceId : null,
      selectedMicId: 'deviceId' in current.mic ? current.mic.deviceId : null,
    }
  }

  async function join(): Promise<void> {
    if (callObject) return
    connectionState.value = 'joining'
    callObject = Daily.createCallObject()

    callObject.on('participant-joined', (ev?: DailyEventObjectParticipant) => ev && upsertParticipant(ev.participant))
    callObject.on('participant-updated', (ev?: DailyEventObjectParticipant) => ev && upsertParticipant(ev.participant))
    callObject.on('participant-left', (ev?: DailyEventObjectParticipantLeft) => ev && removeParticipant(ev.participant))
    callObject.on('track-started', (ev?: DailyEventObjectTrack) => ev?.participant && upsertParticipant(ev.participant))
    callObject.on('track-stopped', (ev?: DailyEventObjectTrack) => ev?.participant && upsertParticipant(ev.participant))
    callObject.on('error', () => { connectionState.value = 'error' })
    callObject.on('available-devices-updated', (_ev?: DailyEventObjectAvailableDevicesUpdated) => { void refreshDevices() })
    callObject.on('selected-devices-updated', (_ev?: DailyEventObjectSelectedDevicesUpdated) => { void refreshDevices() })

    try {
      // role === 'solo' publishes camera + mic; 'shared' publishes mic only (no single
      // camera can represent multiple mates); 'viewer' publishes nothing (subscribe-only).
      await callObject.join({
        url: roomUrl,
        token,
        startVideoOff: role !== 'solo',
        startAudioOff: role === 'viewer',
      })
      connectionState.value = 'joined'
      await refreshDevices()
    } catch (error) {
      connectionState.value = 'error'
      throw error
    }
  }

  async function leave(): Promise<void> {
    if (!callObject) return
    await callObject.leave()
    callObject.destroy()
    callObject = null
    participants.value = new Map()
    connectionState.value = 'left'
    devices.value = EMPTY_DEVICE_STATE
  }

  function setCameraEnabled(enabled: boolean): void {
    if (role === 'viewer' || !callObject) return
    callObject.setLocalVideo(enabled)
    localState.value = { ...localState.value, cameraOn: enabled }
  }

  function setMicEnabled(enabled: boolean): void {
    if (role === 'viewer' || !callObject) return
    callObject.setLocalAudio(enabled)
    localState.value = { ...localState.value, micOn: enabled }
  }

  function setCameraDevice(deviceId: string): void {
    if (role === 'viewer' || !callObject) return
    void callObject.setInputDevicesAsync({ videoDeviceId: deviceId })
  }

  function setMicDevice(deviceId: string): void {
    if (role === 'viewer' || !callObject) return
    void callObject.setInputDevicesAsync({ audioDeviceId: deviceId })
  }

  return {
    join,
    leave,
    participants,
    localState,
    connectionState,
    devices,
    setCameraEnabled,
    setMicEnabled,
    setCameraDevice,
    setMicDevice,
  }
}
